import { Formik } from "formik";
import MsgBoxModal from "../MsgBoxModal/msgbox-modal.component";
import Input from "../Input/input.component";
import CheckBox from "../Checkbox/checkbox.component";
import * as Yup from "yup";
import { useMutation } from "@apollo/client";
import { useToasts } from "react-toast-notifications";
import MsgBoxSelect from "../MsgBoxSelect/msg-box-select.component";
import { useState } from "react";
import { Form, StatusSection, FormRow, FormColumn } from "./styles";
import { UPDATE_WEBHOOK, GET_WEBHOOKS } from "./query";

const WEBHOOK_TYPES = [
	{
		label: "Message",
		value: "message",
	},
	{
		label: "Contact",
		value: "contact",
	},
	{
		label: "API Account",
		value: "apiaccount",
	},
	{
		label: "Broadcast",
		value: "broadcast",
	},
	{
		label: "Inbox",
		value: "inbox",
	},
];

const EditWebhook = ({ isOpen, close, webhook }) => {
	const [updateWebhook, { loading }] = useMutation(UPDATE_WEBHOOK, {
		refetchQueries: [
			{
				query: GET_WEBHOOKS,
			},
		],
		onError: (error) => {
			console.error('GraphQL Error:', error);
		},
	});
	const [dirty, setDirty] = useState(false);

	const { addToast } = useToasts();

	const submit = async ({ targetUrl, type, isCreated, isSent, isDelivered, isRead, isFailed }) => {
		try {
			await updateWebhook({
				variables: {
					id: webhook.ZapierWebHookId,
					targetUrl,
					type: type?.value,
					isCreated,
					isSent,
					isDelivered,
					isRead,
					isFailed,
				},
			});

			addToast("Successfully updated webhook", {
				appearance: "success",
				autoDismiss: true,
			});
			close();
		} catch (e) {
			addToast("Something went wrong", {
				appearance: "error",
				autoDismiss: true,
			});
		}
	};

	const closeModal = () => {
		close();
		setDirty(false);
	};

	const onInputChange = (event, fn) => {
		setDirty(true);
		fn(event);
	};

	// Find the matching type option for the current webhook
	const currentType = WEBHOOK_TYPES.find(type => type.value === webhook.Type);

	return (
		<MsgBoxModal
			aria={{
				labelledby: "heading",
				describedby: "full_description",
			}}
			isOpen={isOpen}
			cancel={closeModal}
			header="Edit Webhook"
			description="Update webhook details and which status updates you want to receive"
			onRequestClose={closeModal}
			submitting={loading}
			submit={submit}
			submitId="edit-webhook"
			submitButtonDisabled={dirty === false}
		>
			<Formik
				initialValues={{
					targetUrl: webhook.Target_Url || "",
					type: currentType || null,
					isCreated: webhook.IsCreated || false,
					isSent: webhook.IsSent || false,
					isDelivered: webhook.IsDelivered || false,
					isRead: webhook.IsRead || false,
					isFailed: webhook.IsFailed || false,
				}}
				validateOnBlur={false}
				validateOnChange={false}
				validationSchema={Yup.object().shape({
					targetUrl: Yup.string().required("Target Url is required"),
					type: Yup.object().required("Type is required"),
				})}
				onSubmit={(values) => {
					submit(values);
				}}
			>
				{({
					handleSubmit,
					values,
					errors,
					handleChange,
					setFieldValue,
				}) => (
					<Form onSubmit={handleSubmit} id="edit-webhook">
						<FormRow>
							<FormColumn>
								<Input
									placeholder="Target Url"
									required
									name="targetUrl"
									label="Target Url"
									onChange={(event) => onInputChange(event, handleChange)}
									error={errors.targetUrl}
									value={values.targetUrl}
								/>
							</FormColumn>
							<FormColumn>
								<MsgBoxSelect
									key={3}
									placeholder="Select a type"
									name="type"
									options={WEBHOOK_TYPES.map((webhookType) => ({
										label: webhookType.label,
										value: webhookType.value,
									}))}
									onChange={(options) => {
										setFieldValue("type", options);
										setDirty(true);
									}}
									label="Type"
									error={errors.type}
									value={values.type}
									hasTooltip
									toolTipText="Select this webhook's type"
								/>
							</FormColumn>
						</FormRow>

						{(values.type?.value === "message" || values.type?.value === "broadcast") && (
							<StatusSection>
								<h4>Status Updates</h4>
								<p>Select which status updates you want to receive:</p>
								<CheckBox
									name="isCreated"
									label="Message Created"
									checked={values.isCreated}
									onChange={(event) => {
										setFieldValue("isCreated", event.target.checked);
										setDirty(true);
									}}
								/>
								<CheckBox
									name="isSent"
									label="Message Sent"
									checked={values.isSent}
									onChange={(event) => {
										setFieldValue("isSent", event.target.checked);
										setDirty(true);
									}}
								/>
								<CheckBox
									name="isDelivered"
									label="Message Delivered"
									checked={values.isDelivered}
									onChange={(event) => {
										setFieldValue("isDelivered", event.target.checked);
										setDirty(true);
									}}
								/>
								<CheckBox
									name="isRead"
									label="Message Read"
									checked={values.isRead}
									onChange={(event) => {
										setFieldValue("isRead", event.target.checked);
										setDirty(true);
									}}
								/>
								<CheckBox
									name="isFailed"
									label="Message Failed"
									checked={values.isFailed}
									onChange={(event) => {
										setFieldValue("isFailed", event.target.checked);
										setDirty(true);
									}}
								/>
							</StatusSection>
						)}
					</Form>
				)}
			</Formik>
		</MsgBoxModal>
	);
};

export default EditWebhook; 